import React, { useMemo, useState } from 'react';
import '../styles/TtnContainer.css';
import {
    TTN_SECTIONS,
    TTN_OPERATION_ROWS,
    TTN_CARGO_COLUMNS,
    makeDefaultTtnFields,
    emptyCargoRow,
    TtnFields,
    TtnCargoRow,
    TtnFieldDef,
} from '../utils/ttnTemplate';
import { buildTtnXlsx, cargoRowHasData, toNum } from '../utils/ttnXlsx';
import { uaHryvniaToWords } from '../utils/uaAmountToWords';
import { ttnPostAPI, ttnUpdateAPI, ttnCreatePdfAPI } from '../http/ttnAPI';

export type TtnEntity = {
    id: number;
    companyId: number;
    number?: string | null;
    date?: string | null;
    /** Об'єкт полів; деякі бекенди можуть віддавати JSON-рядком. */
    fields: TtnFields | string;
    xlsxFile: string | null;
    pdfFile: string | null;
    /** Получатель платежа, к которому привязана ТТН (для печати/подписи). */
    payee?: {
        id: number; name: string;
        stampFile: string | null; signFile: string | null;
        hasStamp: boolean; hasSign: boolean;
    } | null;
    /** Положение печати/подписи получателя платежа (одно на юрлицо), либо дефолт:
     *  { stamp: {cxFrac,cyFrac,widthPt}, sign: {...} }. */
    stampPos?: {
        stamp: { cxFrac: number; cyFrac: number; widthPt: number };
        sign: { cxFrac: number; cyFrac: number; widthPt: number };
    } | null;
    createdBy?: { id: number; username: string } | null;
    createdAt?: string;
};

type Props = {
    companyId: string | number;
    userId: string | number;
    /** Значення, підставлені з картки підприємства (Вантажоодержувач тощо). */
    prefill?: Partial<TtnFields>;
    /** Наявна ТТН — режим перегляду / редагування. */
    existing?: TtnEntity | null;
    readOnly?: boolean;
    /** Викликається після успішного збереження (оновити список, закрити). */
    onSaved: () => void;
    onCancel: () => void;
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const safeParse = (s: string): TtnFields | undefined => {
    try {
        return JSON.parse(s) as TtnFields;
    } catch {
        return undefined;
    }
};

let rowSeq = 0;
const nextRowId = () => ++rowSeq;

const TtnForm: React.FC<Props> = ({
    companyId, userId, prefill, existing, readOnly, onSaved, onCancel,
}) => {
    const initial = useMemo(() => {
        const base = makeDefaultTtnFields();
        const rawExisting = existing?.fields;
        const existingFields: TtnFields | undefined =
            typeof rawExisting === 'string' ? safeParse(rawExisting) : (rawExisting as TtnFields | undefined);
        const src: Partial<TtnFields> = existingFields || prefill || {};
        Object.keys(base).forEach((k) => {
            if (k === 'cargo') return;
            const v = (src as any)[k];
            if (v != null && v !== '') (base as any)[k] = v;
        });
        if (!base.date) base.date = todayISO();
        const srcCargo = (existingFields?.cargo as TtnCargoRow[]) || [];
        const cargo = srcCargo.length ? srcCargo : [emptyCargoRow()];
        return { base, cargo };
    }, [existing, prefill]);

    const [cargoRows, setCargoRows] = useState<Array<{ id: number; data: TtnCargoRow }>>(
        () => initial.cargo.map((data) => ({ id: nextRowId(), data })),
    );
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Підказка суми прописом — оновлюється при зміні числових комірок таблиці.
    const [sumHint, setSumHint] = useState<string>(() =>
        uaHryvniaToWords(initial.cargo.reduce((s, r) => s + toNum(r.sumWithVat), 0)),
    );

    const disabled = !!readOnly || saving;

    const recalcSumHint = (form: HTMLFormElement) => {
        const fd = new FormData(form);
        let total = 0;
        cargoRows.forEach((r) => {
            total += toNum(fd.get(`cargo_${r.id}_sumWithVat`) as string);
        });
        setSumHint(uaHryvniaToWords(total));
    };

    const collectFields = (form: HTMLFormElement): TtnFields => {
        const fd = new FormData(form);
        const fields: TtnFields = {};
        TTN_SECTIONS.forEach((s) =>
            s.fields.forEach((f) => { (fields as any)[f.key] = (fd.get(f.key) as string) ?? ''; }),
        );
        TTN_OPERATION_ROWS.forEach((row) =>
            row.cols.forEach((c) => { (fields as any)[c.key] = (fd.get(c.key) as string) ?? ''; }),
        );
        fields.cargo = cargoRows
            .map((r) => {
                const row: TtnCargoRow = {};
                TTN_CARGO_COLUMNS.forEach((c) => {
                    if (c.auto) return;
                    row[c.key] = (fd.get(`cargo_${r.id}_${c.key}`) as string) ?? '';
                });
                return row;
            })
            .filter(cargoRowHasData);
        return fields;
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (disabled) return;
        setError(null);
        setSaving(true);
        try {
            const fields = collectFields(e.currentTarget);
            const blob = await buildTtnXlsx(fields);
            const res: any = existing
                ? await ttnUpdateAPI(existing.id, fields, blob)
                : await ttnPostAPI(companyId, userId, fields, blob);

            const ok = res && (res.status === 200 || res.status === 201);
            const id = res?.data?.id ?? existing?.id;
            if (!ok || !id) {
                throw new Error('Сервер не принял ТТН');
            }
            await ttnCreatePdfAPI(id);
            onSaved();
        } catch (err: any) {
            setError(err?.message || 'Ошибка сохранения ТТН');
        } finally {
            setSaving(false);
        }
    };

    const renderField = (f: TtnFieldDef) => {
        const dv = (initial.base as any)[f.key] ?? '';
        const common = {
            id: `ttn_${f.key}`,
            name: f.key,
            defaultValue: dv,
            disabled,
            className: 'input ttnForm__input',
            autoComplete: 'off' as const,
        };
        return (
            <label key={f.key} className="ttnForm__field">
                <span className="ttnForm__label">{f.label}</span>
                {f.kind === 'textarea' ? (
                    <textarea {...common} rows={2} />
                ) : (
                    <input {...common} type={f.kind === 'date' ? 'date' : f.kind === 'number' ? 'text' : 'text'} />
                )}
                {f.key === 'totalSumWords' && !readOnly ? (
                    <span className="ttnForm__hint">≈ {sumHint}</span>
                ) : f.hint ? (
                    <span className="ttnForm__hint">{f.hint}</span>
                ) : null}
            </label>
        );
    };

    // Кожен рядок вантажу — окрема картка з сіткою полів, що переноситься на
    // новий рядок при нестачі ширини (замість таблиці з горизонтальним скролом).
    const cargoColumns = TTN_CARGO_COLUMNS.filter((c) => !c.auto);

    const cargoFieldset = (
        <fieldset className="ttnForm__section" disabled={disabled}>
            <legend className="ttnForm__legend">СВЕДЕНИЯ О ГРУЗЕ</legend>
            <div className="ttnForm__cargo-list">
                {cargoRows.map((r, idx) => (
                    <div key={r.id} className="ttnForm__cargo-card">
                        <div className="ttnForm__cargo-card-head">
                            <span className="ttnForm__cargo-card-title">Груз № {idx + 1}</span>
                            {!readOnly && (
                                <button
                                    type="button"
                                    className="nav__button btn-ghost ttnForm__rowbtn"
                                    disabled={disabled || cargoRows.length === 1}
                                    onClick={() =>
                                        setCargoRows((rows) => rows.filter((x) => x.id !== r.id))
                                    }
                                >
                                    ✕
                                </button>
                            )}
                        </div>
                        <div className="ttnForm__grid ttnForm__cargo-grid">
                            {cargoColumns.map((c) => (
                                <label key={c.key} className="ttnForm__field">
                                    <span className="ttnForm__label" title={c.hint}>{c.label}</span>
                                    <input
                                        name={`cargo_${r.id}_${c.key}`}
                                        defaultValue={r.data[c.key] ?? ''}
                                        disabled={disabled}
                                        className="input ttnForm__input"
                                        inputMode={c.numeric ? 'decimal' : undefined}
                                        autoComplete="off"
                                    />
                                </label>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
            {!readOnly && (
                <button
                    type="button"
                    className="nav__button btn-ghost ttnForm__addrow"
                    disabled={disabled}
                    onClick={() =>
                        setCargoRows((rows) => [...rows, { id: nextRowId(), data: emptyCargoRow() }])
                    }
                >
                    + Добавить строку груза
                </button>
            )}
        </fieldset>
    );

    const operationsFieldset = (
        <fieldset className="ttnForm__section" disabled={disabled}>
            <legend className="ttnForm__legend">ПОГРУЗОЧНО-РАЗГРУЗОЧНЫЕ ОПЕРАЦИИ</legend>
            <div className="ttnForm__cargo-wrap">
                <table className="ttnForm__cargo">
                    <thead>
                        <tr>
                            <th>Операція</th>
                            {TTN_OPERATION_ROWS[0].cols.map((c) => (
                                <th key={c.key}>{c.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {TTN_OPERATION_ROWS.map((opRow) => (
                            <tr key={opRow.title}>
                                <td className="ttnForm__op-name">{opRow.title}</td>
                                {opRow.cols.map((c) => (
                                    <td key={c.key}>
                                        <input
                                            name={c.key}
                                            defaultValue={(initial.base as any)[c.key] ?? ''}
                                            disabled={disabled}
                                            className="input ttnForm__cargo-input"
                                            autoComplete="off"
                                        />
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </fieldset>
    );

    return (
        <form className="ttnForm" onSubmit={handleSubmit} onChange={(e) => recalcSumHint(e.currentTarget)}>
            <div className="ttnForm__scroll">
                {TTN_SECTIONS.map((section) => (
                    <React.Fragment key={section.title}>
                        <fieldset className="ttnForm__section" disabled={disabled}>
                            <legend className="ttnForm__legend">{section.title}</legend>
                            <div className="ttnForm__grid">{section.fields.map(renderField)}</div>
                        </fieldset>
                        {section.blockAfter === 'cargo' && cargoFieldset}
                    </React.Fragment>
                ))}

                {operationsFieldset}
            </div>

            <div className="ttnForm__footer">
                {error && <span className="ttnForm__error">{error}</span>}
                {!readOnly && (
                    <button type="submit" className="nav__button btn-primary" disabled={disabled}>
                        {saving ? 'Сохранение…' : existing ? 'Сохранить изменения' : 'Создать ТТН'}
                    </button>
                )}
                <button type="button" className="nav__button btn-ghost" onClick={onCancel} disabled={saving}>
                    {readOnly ? 'Закрыть' : 'Отменить'}
                </button>
            </div>
        </form>
    );
};

export default TtnForm;
